/**
 * SessionDataContext - React Context for managing core session data.
 *
 * Provides:
 * - Current session object
 * - Session loading/error states
 * - Session actions (create, load, clear)
 *
 * Part of the granular context split for performance optimisation.
 * Components that only need session metadata won't re-render when
 * transcript or images change.
 */

/* eslint-disable react-refresh/only-export-components -- Context exports hook alongside provider */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'
import type { Session } from '@/models'
import { getApiClient, type CreateSessionRequest } from '@/services/api'

/**
 * Session data state
 */
export interface SessionDataState {
  session: Session | null
  isLoading: boolean
  error: string | null
}

/**
 * Session data actions
 */
export interface SessionDataActions {
  createSession: (request: CreateSessionRequest) => Promise<Session>
  loadSession: (sessionId: string) => Promise<Session>
  clearSession: () => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
}

/**
 * Session data context value
 */
export interface SessionDataContextValue extends SessionDataState, SessionDataActions {}

const SessionDataContext = createContext<SessionDataContextValue | undefined>(undefined)

/**
 * Session data provider props
 */
export interface SessionDataProviderProps {
  children: ReactNode
}

/**
 * Session data provider component
 *
 * Manages only the session object and its loading/error states.
 * Separated from transcript and images for granular re-renders.
 */
export function SessionDataProvider({ children }: SessionDataProviderProps) {
  const [state, setState] = useState<SessionDataState>({
    session: null,
    isLoading: false,
    error: null,
  })

  const apiClient = getApiClient()

  /**
   * Create new session
   */
  const createSession = useCallback(async (request: CreateSessionRequest): Promise<Session> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await apiClient.createSession(request)
      const session = await apiClient.getSession(response.session_id)

      setState({ session, isLoading: false, error: null })
      return session
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create session'
      console.error('[SessionDataContext] createSession failed', {
        request,
        error: error instanceof Error ? error.stack : error,
      })
      setState(prev => ({ ...prev, isLoading: false, error: message }))
      throw error
    }
  }, [apiClient])

  /**
   * Load existing session
   */
  const loadSession = useCallback(async (sessionId: string): Promise<Session> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const session = await apiClient.getSession(sessionId)
      setState({ session, isLoading: false, error: null })
      return session
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load session'
      console.error('[SessionDataContext] loadSession failed', {
        sessionId,
        error: error instanceof Error ? error.stack : error,
      })
      setState(prev => ({ ...prev, isLoading: false, error: message }))
      throw error
    }
  }, [apiClient])

  /**
   * Clear session state
   */
  const clearSession = useCallback(() => {
    setState({ session: null, isLoading: false, error: null })
  }, [])

  /**
   * Set loading state (for coordinating with other contexts)
   */
  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading }))
  }, [])

  /**
   * Set error state (for coordinating with other contexts)
   */
  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }))
  }, [])

  const value = useMemo(() => ({
    ...state,
    createSession,
    loadSession,
    clearSession,
    setLoading,
    setError,
  }), [state, createSession, loadSession, clearSession, setLoading, setError])

  return (
    <SessionDataContext.Provider value={value}>
      {children}
    </SessionDataContext.Provider>
  )
}

/**
 * Hook to use session data context
 */
export function useSessionData(): SessionDataContextValue {
  const context = useContext(SessionDataContext)

  if (!context) {
    throw new Error('useSessionData must be used within SessionDataProvider')
  }

  return context
}
